import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  CreateQCRecordDTO,
  UpdateQCRecordDTO,
} from "@/modules/qc/domain/contracts/record";
import {
  useCreateQCRecord,
  useQCRecordQuery,
  useQCStandardQuery,
  useUpdateQCRecord,
} from "../api/queries";
import {
  QCRecordFormTour,
  useQCRecordFormTour,
} from "../tour/QCRecordFormTour";
import { QCRecordForm } from "../ui/QCRecordForm";

export function QCRecordPage() {
  const params = useParams({ strict: false });
  const qcStandardId = params.id as string | undefined;
  const recordId = params.recordId as string | undefined;
  const nav = useNavigate();
  const tour = useQCRecordFormTour();
  const isEdit = !!recordId;

  // Get QC Standard
  const { data: qcStandard, isLoading: loadingStandard } = useQCStandardQuery(
    qcStandardId || "",
  );

  // Get existing record if editing
  const { data: existingRecord, isLoading: loadingRecord } = useQCRecordQuery(
    recordId || "",
  );

  const createMutation = useCreateQCRecord();
  const updateMutation = useUpdateQCRecord(recordId || "");

  const handleSubmit = async (data: {
    inspectedAt: string;
    workItemId: string;
    workItemTypeId: string;
    notes: string;
    location: string;
    items: Array<{
      qcStandardItemId: string;
      status: "pass" | "fail";
      notes: string | null;
      photoKey: string | null;
    }>;
    attachments: string[];
  }) => {
    if (!qcStandardId) return;

    if (isEdit && recordId) {
      const updateData: UpdateQCRecordDTO = {
        inspectedAt: data.inspectedAt,
        workItemId: data.workItemId || null,
        workItemTypeId: data.workItemTypeId || null,
        notes: data.notes || null,
        location: data.location || null,
        items: data.items,
        attachments: data.attachments,
      };
      await updateMutation.mutateAsync(updateData);
      // Navigate to QC record detail page after edit
      nav({ to: "/app/qc/records/$recordId", params: { recordId } });
    } else {
      const createData: CreateQCRecordDTO = {
        qcStandardId,
        inspectedAt: data.inspectedAt,
        workItemId: data.workItemId || null,
        workItemTypeId: data.workItemTypeId || null,
        notes: data.notes || null,
        location: data.location || null,
        items: data.items,
        attachments: data.attachments,
      };
      const result = await createMutation.mutateAsync(createData);
      // Navigate to QC record detail page after create, or QC standard page if no result
      if (result?.id) {
        nav({
          to: "/app/qc/records/$recordId",
          params: { recordId: result.id },
        });
      } else {
        nav({ to: `/app/qc/${qcStandardId}` });
      }
    }
  };

  if (loadingStandard || (isEdit && loadingRecord)) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (!qcStandard) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບມາດຕະຖານ QC
              </h2>
              <Button onClick={() => nav({ to: "/app/qc" })} className="mt-4">
                <ArrowLeft className="mr-2 h-4 w-4" />
                ກັບໄປທີ່ລາຍການ QC
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <QCRecordFormTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />

        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => {
                if (isEdit && recordId) {
                  // Navigate to QC record detail page when editing
                  nav({
                    to: "/app/qc/records/$recordId",
                    params: { recordId },
                  });
                } else {
                  // Navigate to QC standard page when creating
                  nav({ to: `/app/qc/${qcStandardId}` });
                }
              }}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              ກັບ
            </Button>
            <div className="text-right">
              <div className="flex items-center justify-end space-x-2">
                <h1 className="font-bold text-xl">{qcStandard.name}</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                {isEdit ? "ແກ້ໄຂຜົນກວດ" : "ບັນທຶກຜົນກວດ QC"}
              </p>
            </div>
          </div>

          {/* Form */}
          <QCRecordForm
            qcStandard={qcStandard}
            existingRecord={existingRecord}
            isEdit={isEdit}
            onSubmit={handleSubmit}
            submitting={createMutation.isPending || updateMutation.isPending}
          />
        </div>
      </Main>
    </>
  );
}
